import PadEditor from '../PadEditor';
// utils
import _ from 'lodash';
import { codeGeneretor } from 'app/utils';
// consts
import { TYPE } from 'app/config/prescription_content';
// form
import { prescriptionTypeInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionTypeForm/utils/prescriptionTypeInitFormFn';
import { prescriptionProcedureInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionProcedureForm/utils/prescriptionProcedureInitFormFn';
import { prescriptionProcedureKitInitFormFn } from 'app/components/modals/pad-editor/prescriptionModal/prescriptionForms/prescriptionProcedureKitForm/utils/prescriptionProcedureKitInitFormFn';
// classes
import { PadPrescriptionType } from './PadPrescriptionType';

/**
 * represents an instance of PadPrescriptionProcedure
 * @class PadPrescriptionProcedure
 * @extends PadEditor
 */
export default class PadPrescriptionProcedure extends PadEditor {
  prepare(currentPadPrescriptionProcedure = [], previousPadPrescriptionProcedure = []) {
    const prepareAuxFn = padPrescription => {
      const id = padPrescription.id;
      const code = padPrescription.code;
      const type = TYPE.SUSTEINED;
      const status = padPrescription.status;
      const padPrescriptionSuggestSched = padPrescription.padPrescriptionSuggestionScheduling;

      padPrescription.suggestScheduling = _.isEmpty(padPrescriptionSuggestSched) ? 0 : 1;

      const prescriptionTypeForm = prescriptionTypeInitFormFn(padPrescription);
      const procedureKitForm = prescriptionProcedureKitInitFormFn(padPrescription);
      const procedureForm = prescriptionProcedureInitFormFn(padPrescription);

      return {
        id,
        code,
        type,
        status,
        prescriptionTypeForm,
        procedureKitForm,
        procedureForm
      };
    };

    const preparedProcedures = currentPadPrescriptionProcedure.map(prepareAuxFn);
    const prePreparedProcedures = previousPadPrescriptionProcedure.map(prepareAuxFn);

    this._currentData = _.cloneDeep(preparedProcedures);
    this._loadedData = _.cloneDeep(prePreparedProcedures);
    this._screenData = _.cloneDeep(preparedProcedures);

    return this;
  }

  /**
   * insert a new padPrescriptionProcedure.
   * @param {object} padPrescriptionProcedure - new padPrescriptionProcedure.
   * @returns {object} the state
   */
  add(padPrescriptionProcedure) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(padPrescriptionProcedure);

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData, currentData });
    const type = padPrescriptionType.getTypeOnAdd(padPrescription);

    padPrescription.id = null;
    padPrescription.type = type;
    padPrescription.code = codeGeneretor();

    this._screenData.push(padPrescription);

    return this;
  }

  /**
   * update a padPrescriptionProcedure.
   * @param {string} code - padPrescriptionProcedure`s code to be edited.
   * @param {object} padPrescriptionProcedure - padPrescriptionProcedure to be edited.
   * @returns {object} return the state
   */
  edit(code, padPrescriptionProcedure = {}) {
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(padPrescriptionProcedure);

    // find index by code
    const indexScreenToEdit = [...this._screenData].findIndex(item => item.code === code);

    Object.entries(padPrescription).forEach(([key, content]) => {
      this._screenData[indexScreenToEdit][key] = content;
    });

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData });
    const type = padPrescriptionType.getTypeOnEdit(padPrescription);

    this._screenData[indexScreenToEdit].type = type;

    return this;
  }

  /**
   * remove a element.
   * @param {string} code - element`s code to remove.
   * @returns {object} return the state
   */
  remove(code) {
    const newScreen = [...this._screenData].filter(currentElement => currentElement.code !== code);

    this._screenData = newScreen;

    return this;
  }

  /**
   * suspend a pad prescription padPrescriptionProcedure.
   * @param {string} code - element`s code to suspend.
   * @returns {object} return the state
   */
  suspend(code) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);

    const padPrescriptionType = new PadPrescriptionType({ screenData, loadedData, currentData });

    const newScreen = [...this._screenData].map(cPadPrescriptionProcedure => {
      // Nada é feito em pads prescrições que não corresponde ao que está sendo suspenso
      if (cPadPrescriptionProcedure.code !== code) {
        return {
          ...cPadPrescriptionProcedure
        };
      }

      // Nada deve ser feito se o pad prescrição já for suspenso. Esse reducer
      // nem deve ser acionado nesses caso, isso se trata de uma validação
      if (cPadPrescriptionProcedure.type === TYPE.SUSPENDED) {
        return {
          ...cPadPrescriptionProcedure
        };
      }

      const type = padPrescriptionType.getTypeOnSuspend(cPadPrescriptionProcedure);

      return {
        ...cPadPrescriptionProcedure,
        type
      };
    });

    this._screenData = newScreen;

    return this;
  }

  /**
   * unsuspend a pad prescription padPrescriptionProcedure.
   * @param {string} code - element`s code to suspend.
   * @returns {object} return the state
   */
  unsuspend(code, prescriptionForm) {
    const currentData = _.cloneDeep(this._currentData);
    const screenData = _.cloneDeep(this._screenData);
    const loadedData = _.cloneDeep(this._loadedData);
    const padPrescription = _.cloneDeep(prescriptionForm);

    const params = { screenData, loadedData, currentData };
    const padPrescriptionType = new PadPrescriptionType(params);

    const newScreen = [...this._screenData].map(cPadPrescriptionProcedure => {
      if (cPadPrescriptionProcedure.code !== code) {
        return {
          ...cPadPrescriptionProcedure
        };
      }

      const padPrescriptionMedicament =
        padPrescriptionType.findPadPrescriptionOnUnsuspend(padPrescription);

      return {
        ...padPrescriptionMedicament
      };
    });

    this._screenData = newScreen;

    return this;
  }

  changeStatus(code) {
    this._screenData = [...this._screenData].map(padPrescriptionProcedure => {
      if (padPrescriptionProcedure.code === code) {
        const status = padPrescriptionProcedure.status === 1 ? 0 : 1;

        return { ...padPrescriptionProcedure, status };
      }

      return { ...padPrescriptionProcedure };
    });

    return this;
  }

  replacesAllPrescriptors(prescriptionForm) {
    const { /* prescriptionTypeForm, */ procedureForm } = prescriptionForm;
    const { formAddOn } = procedureForm;
    const { professional } = formAddOn;

    const nScreen = [...this._screenData].map(prescription => {
      return {
        ...prescription,
        procedureForm: {
          ...prescription.procedureForm,
          alertMessage: {},

          professionalId: professional.id,
          responsibleForPrescription: {
            id: professional.id,
            name: professional.name,
            profession: professional.profession
          },
          formAddOn: {
            ...formAddOn,
            professional: {
              ...professional
            }
          }
        }
      };
    });

    this._screenData = nScreen;
    this._state.data.screen = nScreen;

    return this;
  }
}
